import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import toast from 'react-hot-toast';
import { sortCategories } from '../utils/sortOrder';
import { saveSortOrder } from '../utils/saveSortOrder';

// One category list shared by every useCategories() caller (navbar, shop, category pages),
// so moving around the site reuses it instead of fetching it again each time
const CATEGORIES_CACHE_TTL = 5 * 60 * 1000;
let categoriesCache = { data: null, fetchedAt: 0, request: null };

const loadCategories = (force) => {
  const isFresh = categoriesCache.data && Date.now() - categoriesCache.fetchedAt < CATEGORIES_CACHE_TTL;
  if (isFresh && !force) return Promise.resolve(categoriesCache.data);
  if (categoriesCache.request && !force) return categoriesCache.request;

  const request = supabase.from('categories').select('*')
    .then(({ data, error }) => {
      if (error) throw error;
      // Custom order from the admin (sort_order) first, then alphabetical
      categoriesCache = { data: sortCategories(data || []), fetchedAt: Date.now(), request: null };
      return categoriesCache.data;
    })
    .catch(err => {
      categoriesCache = { ...categoriesCache, request: null };
      throw err;
    });
  categoriesCache = { ...categoriesCache, request };
  return request;
};

// `fresh: true` always refetches on mount (admin dashboard)
export const useCategories = ({ fresh = false } = {}) => {
  const [categories, setCategoriesState] = useState(() => categoriesCache.data || []);
  const [loading, setLoading] = useState(() => !categoriesCache.data || fresh);
  const [error, setError] = useState(null);

  // Keep the shared list in sync with local changes (add/edit/delete/reorder)
  const setCategories = useCallback((updater) => {
    setCategoriesState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      categoriesCache = { ...categoriesCache, data: next, fetchedAt: categoriesCache.fetchedAt || Date.now() };
      return next;
    });
  }, []);

  const fetchCategories = useCallback(async (force = true) => {
    try {
      if (!categoriesCache.data || force) setLoading(true);
      setCategoriesState(await loadCategories(force));
      setError(null);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories(fresh);
  }, [fetchCategories, fresh]);

  // Add a new category
  const addCategory = async (categoryData) => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .insert([categoryData])
        .select()
        .single();

      if (error) throw error;
      
      setCategories(prev => sortCategories([...prev, data]));
      toast.success('Category added successfully!');
      return data;
    } catch (err) {
      console.error('Error adding category:', err);
      toast.error('Error adding category: ' + err.message);
      throw err;
    }
  };

  // Update a category
  const updateCategory = async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setCategories(prev => 
        sortCategories(prev.map(cat => cat.id === id ? data : cat))
      );
      toast.success('Category updated successfully!');
      return data;
    } catch (err) {
      console.error('Error updating category:', err);
      toast.error('Error updating category: ' + err.message);
      throw err;
    }
  };

  // Delete a category
  const deleteCategory = async (id) => {
    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setCategories(prev => prev.filter(cat => cat.id !== id));
      toast.success('Category deleted successfully!');
    } catch (err) {
      console.error('Error deleting category:', err);
      toast.error('Error deleting category: ' + err.message);
      throw err;
    }
  };

  // Save a new order (array of category ids). Updates the list right away and
  // restores the previous order if saving fails.
  const reorderCategories = async (orderedIds) => {
    const previous = categories;
    const position = new Map(orderedIds.map((id, index) => [id, index]));
    setCategories(sortCategories(categories.map(cat => (
      position.has(cat.id) ? { ...cat, sort_order: position.get(cat.id) } : cat
    ))));
    try {
      await saveSortOrder('categories', orderedIds);
      toast.success('Category order saved');
    } catch (err) {
      console.error('Error saving category order:', err);
      setCategories(previous);
      toast.error(err.message);
    }
  };

  // Go back to alphabetical order
  const resetCategoryOrder = async () => {
    const previous = categories;
    setCategories(sortCategories(categories.map(cat => ({ ...cat, sort_order: null }))));
    try {
      await saveSortOrder('categories', previous.map(cat => cat.id), { reset: true });
      toast.success('Categories are back in alphabetical order');
    } catch (err) {
      console.error('Error resetting category order:', err);
      setCategories(previous);
      toast.error(err.message);
    }
  };

  return {
    categories,
    loading,
    error,
    addCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    resetCategoryOrder,
    refreshCategories: fetchCategories
  };
};
